import { isAfter, set } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContactListContext } from '../customer-context/customer-context';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { UseSignalRHook } from '../signalr-context/signalr-context';
import {
  ContractDetails, ContractOrderTypesEnum, ContractProvider, ContractStateContext, ContractTypeEnum, createBackendOrder,
  createLicenseAccessOrder, getISODateString, ILicenseAccessChangeOrder, OrderTypesEnum, SnackBarContext, useSingleContractDispatch
} from '@frontend-workspace/react-shared-lib';
import ContractSaleDialog from '../contract-sale-dialog/contract-sale-dialog';
import AddContractDialog from '../customers/add-contract-dialog/add-contract-dialog';
import EditLicenseDialog from '../edit-license-dialog/edit-license-dialog';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface NewContractContainerProps { }

function NewContractContainerContent(props: NewContractContainerProps) {
  const { companyNumber } = useParams();
  const dispatch = useSingleContractDispatch();
  const navigation = useNavigate();
  const makeRequest = useMakeRequestHook();
  const companies = useContext(ContactListContext);
  const contract = useContext(ContractStateContext);
  const [editLicenseNumber, setEditLicenseNumber] = useState<undefined | number>(undefined);
  const [isSaleModalOpen, setIsSaleModalOpen] = useState<boolean>(false);
  const [isHandlingSale, setIsHandlingSale] = useState<boolean>(false);
  const { setSnackbarMessage } = useContext(SnackBarContext);

  const { data: reasonsForChange } = useGetRequestNewHook(`orders/reasons`, []);
  const { t } = useTranslation();


  const closeEditLicense = () => setEditLicenseNumber(undefined);
  const handleCancel = () => navigation(`/customers/${companyNumber}/all-contracts`);
  const toogleSaleModalOpen = () => setIsSaleModalOpen(!isSaleModalOpen);
  const redirectToNewContract = () => navigation(`/customers/${companyNumber}/all-contracts`);

  const handleDeleteOrder = (id: string) => {
    // do nothing  
  };
  const handleHideOrder = (id: string) => {
    // do nothing
  };

  const handleContractInfoChange = () => {
    dispatch({ type: "NEW_CONTRACT_CONTRACT_INFO_CHANGE" });
    setSnackbarMessage({ type: "success", content: t("newContractContainer.changedContractInformation") });
  };

  UseSignalRHook(redirectToNewContract, [OrderTypesEnum.CreateContract, OrderTypesEnum.CreateSupportContract]);

  const onSale = (licenseAccessChangeOrder: ILicenseAccessChangeOrder) => {
    const isByggdataContract = contract?.contractType === ContractTypeEnum.BYGGDATA;
    if (isByggdataContract && !contract.contractInfo.contractResponsibleEmail) {
      setSnackbarMessage({ type: "error", content: t("newContractContainer.isByggdataContract") });
    }
    else if (contract && companyNumber) {
      const body = {
        activationDate: getISODateString(contract.newSaleDate),
        seatChangesOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.AddSeats, contract),
        newItemsOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.AddProductsToContract, contract),
        terminateLicenseOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.TerminateLicense, contract),
        discountPercentOrder: createBackendOrder(companyNumber, contract.orderItems, ContractOrderTypesEnum.ChangeRecurringDiscountPercent, contract),
        licenseAccessChangeOrder: createLicenseAccessOrder(companyNumber, contract, licenseAccessChangeOrder),
        companyNumber: companyNumber,
        contractNumber: contract.contractNumber,
        contractType: contract.contractType,
        isAcademic: contract.isAcademic
      }
      setSnackbarMessage({ content: t("newContractContainer.startingSale"), type: "info", });
      setIsHandlingSale(true);
      makeRequest(`contracts/action/createNewSale`, 'POST', body).then(result => {
        const isNewFutureContract = contract.newSaleDate && isAfter(contract.newSaleDate, new Date());
        toogleSaleModalOpen();
        if (isNewFutureContract) {
          setSnackbarMessage({ content: `${t("newContractContainer.contractIsCreated")} ${getISODateString(contract.newSaleDate)}`, type: "success" });
          redirectToNewContract();
        }
      });
    }
  };

  useEffect(() => {
    if (contract) {
      makeRequest(`companies/${companyNumber}/userAccounts/forGroup/true/onlyActive/true`)
        .then(result => dispatch({ type: "CONTRACT_EMPLOYEE_LIST_RECEIVED", employeeList: result }))
    }
  }, [!!contract]);

  useEffect(() => {
    if (contract?.invoiceInfo.invoicedCompanyNumber) {
      const selectedCompanyNumber = contract?.invoiceInfo.invoicedCompanyNumber;
      makeRequest(`companies/${selectedCompanyNumber}/userAccounts/forGroup/true/onlyActive/true`)
        .then(result => dispatch({ type: "INVOICE_EMPLOYEE_LIST_RECEIVED", employeeList: result }));
    }
  }, [contract?.invoiceInfo, dispatch]);

  return <>
    <ContractDetails
      onEditLicense={setEditLicenseNumber}
      companies={companies}
      onNewSaleClick={toogleSaleModalOpen}
      onContractInfoChange={handleContractInfoChange}
      onDeleteOrder={handleDeleteOrder}
      onHideOrder={handleHideOrder}
      isHandlingSale={isHandlingSale}
      reasonsForChange={reasonsForChange}
      onReasonChange={(id: string, reason: string) => {
        // do nothing
      }}
      onOpenMoveInvoice={() => {
        // do nothing 
      }}
    />
    {isSaleModalOpen && contract && companyNumber &&
      <ContractSaleDialog
        contract={contract}
        onClose={toogleSaleModalOpen}
        companyNumber={companyNumber}
        onNewSale={onSale}
      />}
    {Number.isInteger(editLicenseNumber) &&
      <EditLicenseDialog
        licenseNumber={editLicenseNumber || 0}
        onClose={closeEditLicense}
      />}
    <AddContractDialog
      open={!contract}
      onClose={handleCancel}
    />
  </>;
}

export function NewContractContainer() {
  return <ContractProvider>
    <NewContractContainerContent />
  </ContractProvider>
}

export default NewContractContainer;